import {useCallback, useEffect, useState} from 'react';

import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {getRulesByVendorId} from 'src/openapi/admin/apis/ApprovalRuleApi';
import {getAvailableGrants} from 'src/openapi/admin/apis/RuleGrantApi';
import {VendorApprovalRule} from 'src/openapi/admin/models/VendorApprovalRule';
import {useAppDispatch} from 'src/store';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {Grant} from 'src/openapi/admin/models/Grant';


export type VendorRulesState = {
    rules: VendorApprovalRule[],
    loadingRules: boolean,
    loadingGrants: boolean,
    error: ApiError | null,
    grants: Grant[],
    reload: () => void,
    reloadGrants: () => void,
    addRuleToState: (rule: VendorApprovalRule) => void,
    removeRuleFromState: (ruleId: string) => void,
};

export const useVendorRules = (vendorId?: string): VendorRulesState => {
    const dispatch = useAppDispatch();
    const shouldLoadRules = Boolean(vendorId);
    const sortRules = useCallback((list: VendorApprovalRule[]) => (
        [...list].sort((a, b) => a.ruleName.localeCompare(b.ruleName, undefined, {sensitivity: 'base'}))
    ), []);

    const {
        data: loadedRules,
        isLoading: loadingRules,
        loadingError: rulesError,
        loadData: reloadRules,
    } = useReadOnlyDataHook<VendorApprovalRule[]>({
        initValue: [],
        shouldLoadData: shouldLoadRules,
        getDispatchAction: useCallback((signal: AbortSignal) => {
            if (!vendorId) {
                return Promise.reject(new Error('Vendor id is required'));
            }
            return dispatch(getRulesByVendorId({vendorId}, {signal}));
        }, [dispatch, vendorId]),
    });

    const {
        data: loadedGrants,
        isLoading: loadingGrants,
        loadData: reloadGrantsData,
    } = useReadOnlyDataHook<Grant[]>({
        initValue: [],
        getDispatchAction: useCallback((signal: AbortSignal) => {
            return dispatch(getAvailableGrants({}, {signal}));
        }, [dispatch]),
    });

    const [rules, setRules] = useState<VendorApprovalRule[]>(sortRules(loadedRules));
    const [grants, setGrants] = useState<Grant[]>(loadedGrants);

    useEffect(() => {
        setRules(sortRules(loadedRules));
    }, [loadedRules, sortRules]);

    useEffect(() => {
        setGrants(loadedGrants);
    }, [loadedGrants]);

    useEffect(() => {
        if (!shouldLoadRules) {
            setRules([]);
        }
    }, [shouldLoadRules]);

    const reload = useCallback(() => {
        if (!shouldLoadRules) {
            setRules([]);
            return;
        }
        reloadRules();
    }, [reloadRules, shouldLoadRules]);

    const reloadGrants = useCallback(() => {
        reloadGrantsData();
    }, [reloadGrantsData]);

    const addRuleToState = useCallback((rule: VendorApprovalRule) => {
        setRules(prevRules => {
            const exists = prevRules.some(existing => existing.ruleId === rule.ruleId);
            if (exists) {
                return sortRules(prevRules.map(existing => existing.ruleId === rule.ruleId ? rule : existing));
            }
            return sortRules([...prevRules, rule]);
        });
    }, [sortRules]);

    const removeRuleFromState = useCallback((ruleId: string) => {
        setRules(prevRules => prevRules.filter(rule => rule.ruleId !== ruleId));
    }, []);

    return {
        rules,
        loadingRules,
        loadingGrants,
        error: shouldLoadRules ? rulesError : null,
        grants,
        reload,
        reloadGrants,
        addRuleToState,
        removeRuleFromState,
    };
};
